import { useT } from "@/i18n/LocaleContext";
import EditorialFigure from "@/components/EditorialFigure";
import CinematicHero from "@/components/CinematicHero";

const IDS = ["aifm", "portfolio", "risk", "governance"];
const IMG = {
  aifm:       "/images/card-aifm.jpg",
  portfolio:  "/images/card-portfolio.jpg",
  risk:       "/images/card-risk.jpg",
  governance: "/images/card-governance.jpg",
};

export const Services = () => {
  const { t } = useT();

  return (
    <>
      <CinematicHero
        imageSrc="/images/services-hero.jpg"
        imageAlt={t("hero_cinematic.services.alt")}
        eyebrow={t("hero_cinematic.services.eyebrow")}
        title={t("hero_cinematic.services.title")}
        subtitle={t("hero_cinematic.services.subtitle")}
        testIdPrefix="services-hero"
      />
      <div id="content" />

      {/* Four alternating image/text service blocks */}
      <section className="bg-cream">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 py-32 md:py-40">
          {IDS.map((id, i) => (
            <div key={id} id={id} data-testid={`service-section-${id}`} className="scroll-mt-32">
              {/* Thin gold rule before non-first blocks */}
              {i > 0 && <div className="rule-thin mb-24 md:mb-32" aria-hidden />}
              {/* Insert bronze detail image between blocks 2 and 3 */}
              {i === 2 && (
                <div className="mb-24 md:mb-32">
                  <EditorialFigure
                    src="/images/services-mid.jpg"
                    alt={t("services_page.detail_alt")}
                    height="h-[42vh] min-h-[300px] max-h-[520px]"
                    testId="services-mid-figure"
                  />
                  <div className="rule-thin mt-24 md:mt-32" aria-hidden />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
                {/* Image */}
                <div
                  className={i % 2 === 1 ? "md:col-span-6 md:col-start-7" : "md:col-span-6 md:col-start-1"}
                >
                  <div className="reveal mask-reveal">
                    <div className="relative w-full aspect-[4/3] overflow-hidden">
                      <div className="absolute inset-0 parallax-inner">
                        <span className="reveal-scale">
                          <img
                            src={IMG[id]}
                            alt={t(`services_cards.${id}.image_alt`)}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Text */}
                <div
                  className={[
                    "cascade",
                    i % 2 === 1
                      ? "md:col-span-5 md:col-start-1 md:row-start-1"
                      : "md:col-span-5 md:col-start-8",
                  ].join(" ")}
                >
                  <div className="reveal flex items-center gap-3">
                    <span className="h-px w-8 bg-gold" aria-hidden />
                    <span
                      className="eyebrow font-sans text-[10.5px] font-medium uppercase text-navy/55"
                      style={{ letterSpacing: "0.28em" }}
                    >
                      {t(`services_page.sections.${id}.number`)}
                    </span>
                  </div>
                  <h2 className="reveal mt-6 font-serif font-normal text-navy text-[30px] md:text-[38px] leading-[1.1]">
                    {t(`services_page.sections.${id}.title`)}
                  </h2>
                  <p className="reveal mt-6 font-serif font-normal text-navy text-[19px] md:text-[21px] leading-[1.7]">
                    {t(`services_page.sections.${id}.body`)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Closing image — marble stair */}
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
          <EditorialFigure
            src="/images/services-close.jpg"
            alt="Refined marble staircase — vertical composition"
            height="h-[54vh] min-h-[340px] max-h-[680px]"
            testId="services-close-figure"
          />
        </div>
      </section>
      <div className="section-bridge-cream-navy" aria-hidden data-testid="services-bridge-footer" />
    </>
  );
};

export default Services;
